<template>
	<Dropdown
		ref="dropdownRef"
		:position="isSmallScreen ? 'bottom-end' : 'bottom-start'"
		@open="onOpen"
		@close="onClose"
	>
		<template #trigger>
			<button
				type="button"
				class="p-1 shrink-0 inline-flex"
				:class="{ 'bg-indigo-200 rounded-md': isActive }"
				:title="$t('Insert link')"
				@click="emitOpenDialog"
				tabindex="-1"
			>
				<LinkIcon class="w-5 h-5" />
			</button>
		</template>

		<template #content>
			<div class="bg-white rounded-lg shadow-lg p-3" style="width: min(20rem, 92vw); max-width: 92vw">
				<div class="flex items-center justify-between mb-2">
					<h3 class="text-sm font-semibold">
						{{ linkMode === "edit" ? $t("Edit link") : $t("Insert link") }}
					</h3>
					<button
						v-if="linkMode === 'edit'"
						type="button"
						class="btn btn-xs btn-primary"
						:title="$t('Remove')"
						@click="onRemoveLink"
					>
						<LinkSlashIcon class="w-4 h-4" />
					</button>
				</div>

				<div class="flex flex-col gap-2">
					<!-- Title -->
					<label class="text-xs">
						<span class="block mb-1">{{ $t("Title") }} ({{ $t("Optional").toLowerCase() }})</span>
						<input
							ref="linkTitleInput"
							:value="linkTitle"
							type="text"
							class="input input-bordered w-full input-sm select-none"
							:placeholder="$t('Text')"
							@input="$emit('update:linkTitle', ($event.target as HTMLInputElement).value)"
						/>
					</label>

					<!-- Link -->
					<label class="text-xs w-full">
						<span class="block mb-1">{{ $t("Link") }}</span>
						<div class="flex items-center gap-2">
							<input
								ref="linkUrlInput"
								:value="linkUrl"
								type="url"
								class="input input-bordered w-full input-sm flex-1 select-none"
								:class="{ 'border-red-400 focus:border-red-500': !isValidUrl && linkUrl }"
								placeholder="https://example.com"
								@input="onUrlInput"
								@keydown.enter.prevent.stop="onSubmit"
							/>
							<button
								v-if="linkMode === 'edit' && linkInfoHref"
								type="button"
								class="btn btn-xs"
								@click="$emit('openExternally')"
							>
								<ArrowTopRightOnSquareIcon class="w-4 h-4" />
							</button>
						</div>
						<!-- Validation message -->
						<p v-if="!isValidUrl && linkUrl" class="text-[0.7rem] text-red-500 mt-1">
							{{ $t("Please enter a valid link") }}
						</p>
					</label>
				</div>

				<!-- Actions -->
				<div class="mt-3 flex justify-end items-center gap-2">
					<button type="button" class="btn btn-xs" @click="onCancel">{{ $t("Cancel") }}</button>
					<button
						type="button"
						class="btn btn-xs btn-primary"
						@click="onSubmit"
						:disabled="!isValidUrl || !linkUrl"
					>
						{{ $t("Save") }}
					</button>
				</div>
			</div>
		</template>
	</Dropdown>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, nextTick } from "vue";
import Dropdown from "@/components/ui/Dropdown.vue";
import { LinkIcon, LinkSlashIcon, ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";

export default defineComponent({
	name: "LinkButton",
	components: { Dropdown, LinkIcon, LinkSlashIcon, ArrowTopRightOnSquareIcon },
	props: {
		editor: { type: Object as PropType<any | null>, required: false },
		isSmallScreen: { type: Boolean, required: true },
		isActive: { type: Boolean, required: true },
		linkTitle: { type: String, required: true },
		linkUrl: { type: String, required: true },
		linkMode: { type: String as PropType<"create" | "edit">, required: true },
		linkInfoHref: { type: String, required: false, default: "" },
	},
	emits: [
		"open",
		"close",
		"openDialog",
		"submit",
		"cancel",
		"openExternally",
		"removeLink",
		"update:linkTitle",
		"update:linkUrl",
	],
	setup() {
		const dropdownRef = ref<any>(null);
		const linkTitleInput = ref<HTMLInputElement | null>(null);
		return { dropdownRef, linkTitleInput };
	},
	computed: {
		trimmedUrl(): string {
			return (this.linkUrl || "").trim();
		},
		isValidUrl(): boolean {
			const v = this.trimmedUrl;
			if (!v) return false;
			const normalized = /^www\./i.test(v) ? `https://${v}` : v;
			if (!/^https?:\/\//i.test(normalized)) return false;
			try {
				const u = new URL(normalized);
				const host = u.hostname;
				if (!host) return false;
				const labels = host.split(".");
				if (labels.length < 2) return false;
				const tld = labels[labels.length - 1];
				if (!/^[A-Za-z]{2,63}$/.test(tld)) return false;
				for (const lbl of labels) {
					if (!/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/.test(lbl)) return false;
				}
				const sld = labels[labels.length - 2] || "";
				if (/^www\./i.test(v) && /^w+$/i.test(sld)) return false;
				return true;
			} catch {
				return false;
			}
		},
	},
	methods: {
		onUrlInput(e: Event) {
			const value = (e.target as HTMLInputElement).value;
			this.$emit("update:linkUrl", value);
		},
		onSubmit() {
			if (!this.isValidUrl) return;
			let url = this.trimmedUrl;
			if (url.toLowerCase().startsWith("www.")) {
				url = `https://${url}`;
				this.$emit("update:linkUrl", url);
			}
			this.$emit("submit");
			(this.$refs.dropdownRef as any)?.close?.();
		},
		onCancel() {
			this.$emit("cancel");
			(this.$refs.dropdownRef as any)?.close?.();
		},
		onRemoveLink() {
			this.$emit("removeLink");
			(this.$refs.dropdownRef as any)?.close?.();
		},
		async open() {
			(this.$refs.dropdownRef as any)?.open?.();
			await nextTick();
		},
		close() {
			(this.$refs.dropdownRef as any)?.close?.();
		},
		emitOpenDialog() {
			this.$emit("openDialog");
		},
		onOpen() {
			this.$emit("open");
			// Focus the input after dropdown's own focus logic
			setTimeout(() => {
				const input = this.$refs.linkUrlInput as HTMLInputElement | null;
				if (input && input.offsetParent !== null) {
					input.focus();
				}
			}, 120);
		},
		onClose() {
			this.$emit("close");
		},
	},
});
</script>
